package fr.manyverse.app.presentation.components.soundboard

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AttachFile
import androidx.compose.material.icons.filled.Info
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TooltipAnchorPosition
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.rememberTooltipState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import fr.manyverse.app.domain.model.AudioData
import fr.manyverse.app.domain.model.SoundTag
import fr.manyverse.app.domain.model.SoundType
import fr.manyverse.app.domain.util.toDisplayNameFromFileName
import kotlinx.coroutines.launch
import fr.manyverse.app.presentation.components.clearFocusOnTap
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.confirm
import manyverse.shared.generated.resources.no_file_selected
import manyverse.shared.generated.resources.select_audio_file
import manyverse.shared.generated.resources.sound_name
import manyverse.shared.generated.resources.sound_name_hint
import manyverse.shared.generated.resources.sound_tags
import manyverse.shared.generated.resources.sound_type
import manyverse.shared.generated.resources.sound_type_info
import manyverse.shared.generated.resources.tag_calm
import manyverse.shared.generated.resources.tag_combat
import manyverse.shared.generated.resources.tag_tension
import manyverse.shared.generated.resources.type_ambient
import manyverse.shared.generated.resources.type_effect
import manyverse.shared.generated.resources.type_music
import manyverse.shared.generated.resources.upload_sound
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun AddSoundDialog(
    showDialog: Boolean,
    selectedAudioData: AudioData?,
    onPickAudio: () -> Unit,
    onDismiss: () -> Unit,
    onConfirm: (name: String, type: SoundType, tags: Set<SoundTag>) -> Unit,
    isLoading: Boolean = false,
    errorMessage: String? = null,
    modifier: Modifier = Modifier
) {
    var name by key(showDialog) { remember { mutableStateOf("") } }
    var selectedType by key(showDialog) { remember { mutableStateOf(SoundType.MUSIC) } }
    var selectedTags by key(showDialog) { remember { mutableStateOf<Set<SoundTag>>(emptySet()) } }
    var typeDropdownExpanded by remember { mutableStateOf(false) }
    val tooltipState = rememberTooltipState()
    val coroutineScope = rememberCoroutineScope()

    // Nom pré-rempli depuis le fichier choisi tant que l'utilisateur n'en a pas saisi
    LaunchedEffect(selectedAudioData) {
        val fileName = selectedAudioData?.fileName ?: return@LaunchedEffect
        if (name.isBlank()) name = fileName.toDisplayNameFromFileName().take(100)
    }

    if (showDialog) {
        AlertDialog(
            properties = DialogProperties(dismissOnBackPress = false, dismissOnClickOutside = false),
            onDismissRequest = {
                if (!isLoading) {
                    onDismiss()
                }
            },
            title = {
                Text(stringResource(Res.string.upload_sound))
            },
            text = {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState()),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    // Audio file picker button
                    OutlinedButton(
                        onClick = onPickAudio,
                        enabled = !isLoading,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Icon(
                            imageVector = Icons.Default.AttachFile,
                            contentDescription = null,
                            modifier = Modifier.size(18.dp)
                        )
                        Text(
                            text = selectedAudioData?.fileName
                                ?: stringResource(Res.string.select_audio_file),
                            modifier = Modifier.padding(start = 8.dp)
                        )
                    }

                    if (selectedAudioData == null) {
                        Text(
                            text = stringResource(Res.string.no_file_selected),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }

                    // Sound name input
                    OutlinedTextField(
                        value = name,
                        onValueChange = { if (it.length <= 100) name = it },
                        label = { Text(stringResource(Res.string.sound_name)) },
                        placeholder = { Text(stringResource(Res.string.sound_name_hint)) },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        enabled = !isLoading,
                        supportingText = {
                            Text("${name.length}/100")
                        },
                        keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences)
                        )

                    // Sound type dropdown with info tooltip
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        ExposedDropdownMenuBox(
                            expanded = typeDropdownExpanded && !isLoading,
                            onExpandedChange = {
                                typeDropdownExpanded = !typeDropdownExpanded && !isLoading
                            },
                            modifier = Modifier.weight(1f)
                        ) {
                            OutlinedTextField(
                                value = when (selectedType) {
                                    SoundType.MUSIC -> stringResource(Res.string.type_music)
                                    SoundType.EFFECT -> stringResource(Res.string.type_effect)
                                    SoundType.AMBIENT -> stringResource(Res.string.type_ambient)
                                },
                                onValueChange = {},
                                readOnly = true,
                                label = { Text(stringResource(Res.string.sound_type)) },
                                trailingIcon = {
                                    ExposedDropdownMenuDefaults.TrailingIcon(expanded = typeDropdownExpanded)
                                },
                                colors = ExposedDropdownMenuDefaults.outlinedTextFieldColors(),
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .menuAnchor(
                                        ExposedDropdownMenuAnchorType.PrimaryEditable,
                                        true
                                    ),
                                enabled = !isLoading
                            )
                            ExposedDropdownMenu(
                                expanded = typeDropdownExpanded,
                                onDismissRequest = { typeDropdownExpanded = false }
                            ) {
                                DropdownMenuItem(
                                    text = { Text(stringResource(Res.string.type_music)) },
                                    onClick = {
                                        selectedType = SoundType.MUSIC
                                        typeDropdownExpanded = false
                                    }
                                )
                                DropdownMenuItem(
                                    text = { Text(stringResource(Res.string.type_ambient)) },
                                    onClick = {
                                        selectedType = SoundType.AMBIENT
                                        typeDropdownExpanded = false
                                    }
                                )
                                DropdownMenuItem(
                                    text = { Text(stringResource(Res.string.type_effect)) },
                                    onClick = {
                                        selectedType = SoundType.EFFECT
                                        typeDropdownExpanded = false
                                    }
                                )
                            }
                        }

                        // Info tooltip
                        TooltipBox(
                            positionProvider = TooltipDefaults.rememberTooltipPositionProvider(
                                TooltipAnchorPosition.Above
                            ),
                            tooltip = {
                                PlainTooltip {
                                    Text(stringResource(Res.string.sound_type_info))
                                }
                            },
                            state = tooltipState
                        ) {
                            IconButton(onClick = { coroutineScope.launch { tooltipState.show() } }) {
                                Icon(
                                    imageVector = Icons.Default.Info,
                                    contentDescription = stringResource(Res.string.sound_type_info),
                                    tint = MaterialTheme.colorScheme.primary
                                )
                            }
                        }
                    }

                    // Tags selection
                    Column {
                        Text(
                            text = stringResource(Res.string.sound_tags),
                            style = MaterialTheme.typography.labelMedium
                        )
                        FlowRow(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            modifier = Modifier.padding(top = 8.dp)
                        ) {
                            SoundTag.entries.forEach { tag ->
                                val palette = soundTagPalette(tag)
                                val selected = tag in selectedTags
                                Box(
                                    modifier = Modifier
                                        .background(
                                            palette.container.copy(alpha = if (selected) 1f else 0.4f),
                                            RoundedCornerShape(4.dp)
                                        )
                                        .clickable(enabled = !isLoading) {
                                            selectedTags = if (selected) {
                                                selectedTags - tag
                                            } else {
                                                selectedTags + tag
                                            }
                                        }
                                        .padding(horizontal = 10.dp, vertical = 6.dp)
                                ) {
                                    Text(
                                        text = when (tag) {
                                            SoundTag.CALM -> stringResource(Res.string.tag_calm)
                                            SoundTag.COMBAT -> stringResource(Res.string.tag_combat)
                                            SoundTag.TENSION -> stringResource(Res.string.tag_tension)
                                        },
                                        style = MaterialTheme.typography.labelSmall,
                                        color = palette.label.copy(alpha = if (selected) 1f else 0.5f)
                                    )
                                }
                            }
                        }
                    }

                    // Error message
                    if (errorMessage != null) {
                        Text(
                            text = errorMessage,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodySmall
                        )
                    }

                    // Loading indicator
                    if (isLoading) {
                        LinearProgressIndicator(
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
            },
            confirmButton = {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    TextButton(
                        onClick = onDismiss,
                        enabled = !isLoading
                    ) {
                        Text(stringResource(Res.string.cancel))
                    }
                    Button(
                        onClick = {
                            onConfirm(name.trim(), selectedType, selectedTags)
                        },
                        enabled = !isLoading && selectedAudioData != null && name.isNotBlank()
                    ) {
                        Text(stringResource(Res.string.confirm))
                    }
                }
            },
            modifier = modifier.clearFocusOnTap()
        )
    }
}
